import { useMemo, useState, type ReactNode } from "react";
import type { Key } from "react-aria-components";
import { Button, Header, ListBox, Popover, SearchField, Skeleton } from "@heroui/react";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Ticker } from "@/hooks/useTickers";
import { fmt, STAGE_META, type DeskRow, type Stage, type Universe } from "./deskData";
import { favorites, useFavorites } from "./favorites";
import {
  buildRow, changeText, compactUsd, matches, passesStage, sortRows,
  type CoinRow, type SortDir, type SortKey, type StageFilter,
} from "./coinList";

/** Stage words in the row: colour only where there's something to do. */
const STAGE_TEXT: Record<Stage, string> = {
  ready: "font-semibold text-profit",
  armed: "font-semibold text-warning",
  signal: "font-semibold text-foreground",
  blocked: "text-danger",
  stalking: "text-muted",
  "no-bias": "text-muted",
};

const STAGE_CHIPS: { id: StageFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "ready", label: "Ready" },
  { id: "armed", label: "Almost" },
  { id: "stalking", label: "Waiting" },
];

/** Search results outside today's list: the busiest matches. */
const MAX_OTHERS = 30;
const TAB_PREF = "vp.desk.coinTab";
const GRID = "grid w-full grid-cols-[minmax(0,1fr)_auto_4.75rem] items-center gap-2";

export interface MarketListProps {
  /** Today's coins, base names ("SOL"). */
  coins: string[];
  universe: Universe | null;
  rowBySymbol: Map<string, DeskRow>;
  readySet: Set<string>;
  tickers: Map<string, Ticker> | null;
  /** "SOLUSDT" */
  selected: string;
  onSelect: (symbol: string) => void;
  loading: boolean;
  /** "panel": the desk's coin column. "popover": the chart's pair picker. */
  variant?: "panel" | "popover";
  className?: string;
}

function TabButton({ on, onPress, children }: { on: boolean; onPress: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      onClick={onPress}
      className={cn(
        "relative h-9 text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus",
        on ? "tab-bar font-semibold text-foreground" : "text-muted hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function SortButton({
  label, k, sort, onSort, align = "left",
}: { label: string; k: SortKey; sort: { key: SortKey; dir: SortDir }; onSort: (k: SortKey) => void; align?: "left" | "right" }) {
  const on = sort.key === k;
  const Icon = !on ? ArrowUpDown : sort.dir === "desc" ? ArrowDown : ArrowUp;
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      aria-label={`Sort by ${label}${on ? (sort.dir === "desc" ? ", highest first" : ", lowest first") : ""}`}
      className={cn(
        "flex items-center gap-1 rounded outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-focus",
        align === "right" && "justify-self-end",
        on && "text-foreground",
      )}
    >
      {label}
      <Icon className={cn("size-3", !on && "opacity-50")} />
    </button>
  );
}

function CoinItem({ r, loading }: { r: CoinRow; loading: boolean }) {
  const up = (r.changePct ?? 0) >= 0;
  return (
    <ListBox.Item
      id={r.symbol}
      textValue={r.base}
      className={cn(
        "rounded-none px-3 py-2",
        "data-[selected=true]:bg-default data-[selected=true]:shadow-[inset_2px_0_0_var(--brand)]",
      )}
    >
      <div className={GRID}>
        <div className="min-w-0">
          <div className="flex items-baseline gap-1">
            <span className="text-[13px] font-semibold text-foreground">{r.base}</span>
            <span className="text-2xs text-muted">/USDT</span>
            {r.favorite && <Star className="size-3 self-center fill-warning text-warning" aria-label="favourite" />}
          </div>
          <div className="truncate text-2xs">
            {r.stage ? (
              <>
                <span className={STAGE_TEXT[r.stage]}>{STAGE_META[r.stage].label}</span>
                {r.setup && (
                  <span className="text-muted">
                    {" · "}{r.setup.label}{" "}
                    <span className={r.setup.dir === "LONG" ? "text-profit" : "text-loss"}>{r.setup.dir === "LONG" ? "↑" : "↓"}</span>
                  </span>
                )}
              </>
            ) : r.today ? (
              loading ? <span className="text-muted">Scanning…</span> : <span className="text-muted">—</span>
            ) : (
              <span className="text-muted">Not in today's scan</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="num text-[13px] text-foreground">{r.price === null ? "—" : fmt(r.price)}</div>
          <div className="num text-2xs text-muted">{compactUsd(r.volume)}</div>
        </div>
        <span
          className={cn(
            "num rounded-md py-1 text-center text-xs font-semibold",
            r.changePct === null ? "bg-default text-muted" : up ? "bg-profit text-on-profit" : "bg-loss text-on-loss",
          )}
        >
          {changeText(r.changePct)}
        </span>
      </div>
    </ListBox.Item>
  );
}

/**
 * The coin list (Binance's markets panel): search every USDT coin, today's
 * coins or your ★ favourites, stage chips, sortable columns, and rows with
 * the 24h change as a solid pill.
 */
export function MarketList({
  coins, universe, rowBySymbol, readySet, tickers, selected, onSelect, loading, variant = "panel", className,
}: MarketListProps) {
  const favs = useFavorites();
  const [query, setQuery] = useState("");
  const [tab, setTabState] = useState<"today" | "fav">(() => {
    try { return localStorage.getItem(TAB_PREF) === "fav" ? "fav" : "today"; } catch { return "today"; }
  });
  const setTab = (t: "today" | "fav") => {
    setTabState(t);
    try { localStorage.setItem(TAB_PREF, t); } catch { /* ignore */ }
  };
  const [stageFilter, setStageFilter] = useState<StageFilter>("all");
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: "stage", dir: "desc" });
  const onSort = (k: SortKey) =>
    setSort(s => (s.key === k ? (s.dir === "desc" ? { key: k, dir: "asc" } : { key: "stage", dir: "desc" }) : { key: k, dir: "desc" }));

  const todaySet = useMemo(() => new Set(coins.map(c => `${c}USDT`)), [coins]);
  const volumeM = useMemo(() => new Map((universe?.included ?? []).map(c => [c.symbol, c.volumeM])), [universe]);
  const make = (symbol: string) =>
    buildRow(symbol, {
      row: rowBySymbol.get(symbol),
      ready: readySet.has(symbol),
      ticker: tickers?.get(symbol),
      volumeM: volumeM.get(symbol),
      favorite: favs.includes(symbol),
      today: todaySet.has(symbol),
    });
  const todayRows = useMemo(() => [...todaySet].map(make), [todaySet, rowBySymbol, readySet, tickers, volumeM, favs]);
  const favRows = useMemo(() => favs.map(make), [favs, rowBySymbol, readySet, tickers, volumeM, todaySet]);

  const searching = query.trim() !== "";
  const shown = useMemo(() => {
    if (searching) {
      const mine = [...todayRows, ...favRows.filter(r => !r.today)].filter(r => matches(r, query));
      return sortRows(mine, sort.key, sort.dir);
    }
    const base = tab === "fav" ? favRows : todayRows.filter(r => passesStage(r, stageFilter));
    return sortRows(base, sort.key, sort.dir);
  }, [searching, query, tab, todayRows, favRows, stageFilter, sort]);
  // Searching: busy coins outside your lists, so any coin can be charted.
  const others = useMemo(() => {
    if (!searching || !tickers) return [];
    const known = new Set(shown.map(r => r.symbol));
    const hits: Ticker[] = [];
    for (const t of tickers.values()) {
      if (!known.has(t.symbol) && matches({ base: t.symbol.replace(/USDT$/, "") }, query)) hits.push(t);
    }
    hits.sort((a, b) => b.volume - a.volume);
    return hits.slice(0, MAX_OTHERS).map(t => make(t.symbol));
  }, [searching, query, tickers, shown]);

  const stageCount = (f: StageFilter) => todayRows.filter(r => passesStage(r, f)).length;
  const select = (k: Key) => onSelect(String(k));
  const popover = variant === "popover";

  return (
    <section
      aria-label="Coins"
      className={cn(
        "flex min-h-0 flex-col overflow-hidden",
        !popover && "rounded-[1rem] border border-border bg-surface",
        className,
      )}
    >
      <div className="px-3 pt-3">
        <SearchField aria-label="Search coins" value={query} onChange={setQuery} autoFocus={popover}>
          <SearchField.Group>
            <SearchField.SearchIcon />
            <SearchField.Input placeholder="Search coins, e.g. ETH" className="text-sm" />
            <SearchField.ClearButton />
          </SearchField.Group>
        </SearchField>
      </div>

      {!searching && (
        <>
          <div role="tablist" aria-label="Coin lists" className="flex gap-4 border-b border-separator px-3">
            <TabButton on={tab === "today"} onPress={() => setTab("today")}>
              Today's coins <span className="num text-muted">{todayRows.length}</span>
            </TabButton>
            <TabButton on={tab === "fav"} onPress={() => setTab("fav")}>
              ★ Favorites <span className="num text-muted">{favRows.length}</span>
            </TabButton>
          </div>
          {tab === "today" && (
            <div className="flex flex-wrap gap-1.5 px-3 pt-2" role="group" aria-label="Show coins at stage">
              {STAGE_CHIPS.map(c => {
                const on = stageFilter === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setStageFilter(c.id)}
                    className={cn(
                      "h-6 rounded-md px-2 text-2xs outline-none transition-colors focus-visible:ring-2 focus-visible:ring-focus",
                      on ? "bg-default font-semibold text-foreground" : "text-muted hover:text-foreground",
                    )}
                  >
                    {c.label} <span className="num">{stageCount(c.id)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      <div className={cn(GRID, "px-3 pb-1.5 pt-2.5 text-2xs text-muted")}>
        <SortButton label="Coin / Vol" k="vol" sort={sort} onSort={onSort} />
        <SortButton label="Price" k="price" sort={sort} onSort={onSort} align="right" />
        <SortButton label="24h" k="chg" sort={sort} onSort={onSort} align="right" />
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        {coins.length === 0 && !searching && tab === "today" ? (
          <div className="space-y-2 px-3 pb-3" aria-busy={loading}>
            <p className="text-xs text-muted">{loading ? "Picking today's busiest coins for day trading…" : "Today's list isn't available yet. Press Scan to try again."}</p>
            {loading && Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}
          </div>
        ) : shown.length === 0 && others.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-muted">
            {searching
              ? tickers ? `No coin called "${query.trim()}" on Binance.` : "No match in your lists."
              : tab === "fav"
                ? "No favorites yet. Press ☆ next to a coin's name above the chart to add it."
                : "No coins at this stage right now."}
          </p>
        ) : (
          <ListBox
            aria-label={searching ? "Search results" : tab === "fav" ? "Favorite coins" : "Today's coins"}
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={new Set<Key>([selected])}
            onSelectionChange={keys => {
              if (keys === "all") return;
              const k = [...keys][0];
              if (k != null) select(k);
            }}
            className="p-0 pb-1"
          >
            {shown.length > 0 && (
              <ListBox.Section>
                {shown.map(r => <CoinItem key={r.symbol} r={r} loading={loading} />)}
              </ListBox.Section>
            )}
            {others.length > 0 && (
              <ListBox.Section className="mt-1 border-t border-separator pt-1">
                <Header className="px-3 py-1.5 text-3xs font-semibold uppercase tracking-wider text-muted">Other coins</Header>
                {others.map(r => <CoinItem key={r.symbol} r={r} loading={loading} />)}
              </ListBox.Section>
            )}
          </ListBox>
        )}
        {!tickers && (
          <p className="px-3 pb-2 pt-1 text-3xs text-muted">24h change loads with the market data.</p>
        )}
      </div>
    </section>
  );
}

/** "SOL/USDT ▾": the coin list as a dropdown (Binance mobile's pair picker). */
export function CoinPicker(props: Omit<MarketListProps, "variant" | "className">) {
  const [open, setOpen] = useState(false);
  const base = props.selected.replace(/USDT$/, "");
  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Button size="sm" variant="ghost" aria-label={`Coin: ${base}. Change coin`} className="-ml-2 gap-1 px-2">
        <span className="text-sm font-semibold">{base}</span>
        <span className="text-xs text-muted">/USDT</span>
        <ChevronDown className="size-3.5 text-muted" />
      </Button>
      <Popover.Content placement="bottom start" className="w-[23rem] overflow-hidden p-0">
        <Popover.Dialog aria-label="Choose a coin" className="flex h-[28rem] flex-col p-0">
          <MarketList
            {...props}
            variant="popover"
            className="h-full"
            onSelect={s => {
              props.onSelect(s);
              setOpen(false);
            }}
          />
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/** ☆ / ★ next to the coin's name: add or remove it from your favorites. */
export function FavoriteStar({ symbol }: { symbol: string }) {
  const favs = useFavorites();
  const on = favs.includes(symbol);
  const base = symbol.replace(/USDT$/, "");
  return (
    <Button
      isIconOnly
      size="sm"
      variant="ghost"
      aria-pressed={on}
      aria-label={on ? `Remove ${base} from favorites` : `Add ${base} to favorites`}
      onPress={() => favorites.toggle(symbol)}
      className={cn(on ? "text-warning" : "text-muted hover:text-foreground")}
    >
      <Star className={cn("size-4", on && "fill-warning")} />
    </Button>
  );
}
